import React, { useState, useEffect, useMemo } from "react";
import { Checkbox, Switch, Tooltip } from "antd";
import classNames from "classnames";
import ModalBox from "@components/ui-kit/ModalBox";
import { nameChoosedMapping } from "../utils";
import { WIDGET_METRICS_CONFIG } from "@services/math/math.const";
import styles from "./styles.module.scss";
import { useTimeRangeStore } from "src/store/daily-week";

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  setDataPoints: (data: Record<string, boolean>) => void;
  dataPoints: Record<string, boolean>;
  showForecast: boolean;
  setShowForecast: (b: boolean) => void;
  isPoModal: boolean;
  setIsPoModal: (b: boolean) => void;
  isInventory?: boolean;
  isFinance?: boolean;
  isCash?: boolean;
  isTableView?: boolean;
  setIsTableView?: (b: boolean) => void;
}

const METRIC_GROUPS: Record<string, string[]> = {
  Amazon: [
    "sponsored_brands",
    "sponsored_display",
    "sponsored_products",
    "amazon_sales",
    "amazon_ad_sales",
    "amazon_other_sales",
    "amazon_organic_sales",
    "amazon_opex",
    "amazon_cogs",
    "amazon_cost",
    "amazon_fees",
  ],
  Shopify: [
    "shopify_sales",
    "shopify_ad_sales",
    "shopify_other_sales",
    "shopify_cogs",
    "shopify_cost",
    "shopify_fees",
    "shopify_opex",
  ],
  TikTok: [
    "tiktok_sales",
    "tiktok_ad_sales",
    "tiktok_other_sales",
    "tiktok_cogs",
    "tiktok_cost",
    "tiktok_fees",
    "tiktok_opex",
  ],
};

export const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  onClose,
  setDataPoints,
  dataPoints,
  showForecast,
  setShowForecast,
  isPoModal,
  setIsPoModal,
  isInventory = false,
  isFinance = false,
  isCash = false,
  isTableView = false,
  setIsTableView,
}) => {
  const [forecastChecked, setForecastChecked] = useState(showForecast);
  const [poModalChecked, setPoModalChecked] = useState(isPoModal);
  const [tableViewChecked, setTableViewChecked] = useState(isTableView);
  const [choosedDataPoints, setChoosedDataPoints] = useState(dataPoints);

  useEffect(() => {
    if (open) {
      setForecastChecked(showForecast);
      setPoModalChecked(isPoModal);
      setTableViewChecked(isTableView);
      setChoosedDataPoints(dataPoints);
    }
  }, [open, showForecast, isPoModal, isTableView, dataPoints]);

  const { timeRange, setTimeRange } = useTimeRangeStore();

  const handleApply = () => {
    setShowForecast(forecastChecked);
    setIsPoModal(poModalChecked);
    setIsTableView?.(tableViewChecked);
    setDataPoints(choosedDataPoints);
    onClose();
  };

  const handleReset = () => {
    setShowForecast(false);
    setIsPoModal(false);
    setIsTableView?.(false);
    if (isInventory || isCash) {
      const resetPoints: Record<string, boolean> = {};
      Object.keys(choosedDataPoints).forEach((k) => (resetPoints[k] = true));
      setDataPoints(resetPoints);
      setTimeRange("week");
    } else {
      setDataPoints(nameChoosedMapping);
    }
    onClose();
  };

  const handleGroupChange = (groupKeys: string[], checked: boolean) => {
    setChoosedDataPoints((prev) => {
      const updated = { ...prev };
      groupKeys.forEach((key) => {
        if (key in updated) updated[key] = checked;
      });
      return updated;
    });
  };

  const handleMetricChange = (key: string, checked: boolean) => {
    setChoosedDataPoints((prev) => ({ ...prev, [key]: checked }));
  };

  const groupedMetrics = useMemo(() => {
    const groups: Record<string, string[]> = {
      General: [],
      Amazon: [],
      Shopify: [],
      TikTok: [],
    };
    const platformMap: Record<string, string> = {};
    Object.entries(METRIC_GROUPS).forEach(([group, keys]) => {
      keys.forEach((k) => (platformMap[k] = group));
    });
    Object.keys(choosedDataPoints).forEach((key) => {
      const grp = platformMap[key] || "General";
      groups[grp]?.push(key);
    });
    return Object.entries(groups).filter(([, keys]) => keys.length > 0);
  }, [choosedDataPoints]);

  const renderMetric = (key: string) => {
    const label = key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace("Cogs", "COGS")
      .replace("Opex", "OPEX");
    return (
      <div key={key} className={styles.row}>
        <Checkbox
          checked={choosedDataPoints[key]}
          onChange={(e) => handleMetricChange(key, e.target.checked)}
        />
        <span>{label}</span>
        <Tooltip title={WIDGET_METRICS_CONFIG[key]?.info} placement="right">
          <button>i</button>
        </Tooltip>
      </div>
    );
  };

  const renderGroup = ([groupName, groupKeys]: [string, string[]]) => {
    const allChecked = groupKeys.every((k) => choosedDataPoints[k]);
    const someChecked = groupKeys.some((k) => choosedDataPoints[k]);
    return (
      <div key={groupName} className={styles.metricGroup}>
        <div className={classNames(styles.groupHeader, styles.row)}>
          <Checkbox
            checked={allChecked}
            indeterminate={!allChecked && someChecked}
            onChange={(e) => handleGroupChange(groupKeys, e.target.checked)}
          />
          <span className={styles.groupTitle}>{groupName}</span>
        </div>
        <div className={styles.groupMetrics}>
          {groupKeys.map((key) => renderMetric(key))}
        </div>
      </div>
    );
  };

  return (
    <ModalBox
      title="Profit and Loss Statement"
      isOpen={open}
      onClose={onClose}
      className={classNames(styles.modal, {
        [styles.cash]: isInventory || isCash,
        [styles.cashFlow]: isCash,
      })}
    >
      <p>Choose view and metrics for Profit and Loss Statement</p>

      {(isInventory || isCash) && (
        <div className={styles.tabContainer}>
          <button
            className={classNames(
              styles.tabItem,
              timeRange === "day" && styles.active,
            )}
            onClick={() => setTimeRange("day")}
          >
            Day
          </button>
          <button
            className={classNames(
              styles.tabItem,
              timeRange === "week" && styles.active,
            )}
            onClick={() => setTimeRange("week")}
          >
            Week
          </button>
          <button
            className={classNames(
              styles.tabItem,
              timeRange === "month" && styles.active,
            )}
            onClick={() => setTimeRange("month")}
          >
            Month
          </button>
        </div>
      )}

      <div className={styles.switchRow}>
        {!isFinance && (
          <div className={styles.switchContainer}>
            <Switch checked={forecastChecked} onChange={setForecastChecked} />
            <span className={styles.switchLabel}>Show Forecast</span>
          </div>
        )}
        {(isInventory || isCash) && setIsTableView && (
          <div className={styles.switchContainer}>
            <Switch checked={tableViewChecked} onChange={setTableViewChecked} />
            <span className={styles.switchLabel}>Table View</span>
          </div>
        )}
      </div>

      <div className={styles.points}>{groupedMetrics.map(renderGroup)}</div>

      <div className={styles.buttons}>
        <button className={styles.reset} onClick={handleReset}>
          reset to default
        </button>
        <button className={styles.apply} onClick={handleApply}>
          Apply
        </button>
      </div>
    </ModalBox>
  );
};
